import { useInputsWithChoices } from "@/features/dictionaries/useInputsWithChoices";
import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";
import {
    LogicConditionFields,
    collectPriorInputs,
    defaultOperator,
    defaultRuleValue,
    type PriorLogicInput,
} from "@/features/forms/components/form-builder/LogicConditionFields";
import { useSelectDiaryLabels } from "@/features/diaries/api/selectDiaryLabels";
import { BuilderStepOff } from "@/features/forms/components/form-builder/BuilderStepOff";
import { useBuilderAutoSave } from "@/features/forms/components/form-builder/useAutoSave";
import { useScreenerCopy } from "@/features/i18n/screenerCopy";
import {
    useCreateScreener,
    useDeleteScreener,
    useFormScreener,
    useUpdateScreener,
} from "@/features/screeners/api/screenerBuilderApi";
import {
    BROWSER_SCORE_ENGINES,
    parseScreenerCriteria,
    type PageLogicCondition,
    type ScreenerCriteria,
} from "@patient-watch/forms";
import type { InputT, ScreenerT } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@patient-watch/ui/alert-dialog";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import { toast } from "@patient-watch/ui/sonner";
import { Switch } from "@patient-watch/ui/switch";
import { Copy, ExternalLink, ListChecks, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

/** Form builder Screener tab: turn this form into an eligibility check that templates can open before sign-up. */
export function FormScreenerSettings({
    form,
    formId,
    canEdit,
}: {
    form: FormBuilderData;
    formId: string;
    /** Only admins can set up screeners. */
    canEdit: boolean;
}) {
    const { data: screener, isLoading } = useFormScreener(formId);
    const createScreener = useCreateScreener(formId);

    return (
        <section className="bg-background border rounded-xl overflow-hidden" data-testid="form-screener-settings">
            <div className="px-5 py-4 border-b">
                <h2 className="font-semibold text-sm">Screener</h2>
                <p className="text-muted-foreground text-xs text-pretty">
                    Check a patient is eligible before they sign up. Pick the screener on a template&apos;s Consent
                    step, and that template&apos;s sign-up link opens it first.
                </p>
            </div>

            {isLoading ? (
                <div className="space-y-2 p-6">
                    <ui.Skeleton className="w-1/2 h-4" />
                    <ui.Skeleton className="w-full h-8" />
                </div>
            ) : !canEdit ? (
                <p className="p-6 text-muted-foreground text-sm">Only admins can set up screeners.</p>
            ) : screener ? (
                <ScreenerEditor key={screener.screener_id} form={form} formId={formId} screener={screener} />
            ) : (
                <BuilderStepOff
                    icon={ListChecks}
                    title="Not a screener"
                    description="Keep screener forms unpublished so they stay out of the form library."
                    testId="form-screener-off"
                >
                    <ui.Button
                        type="button"
                        size="sm"
                        disabled={createScreener.isPending}
                        onClick={() => createScreener.mutate()}
                        data-testid="screener-create"
                    >
                        Use as a screener
                    </ui.Button>
                </BuilderStepOff>
            )}
        </section>
    );
}

interface ScreenerDraft {
    is_active: boolean;
    label_id: string | null;
    criteria: ScreenerCriteria;
    eligible_title: string;
    eligible_body: string;
    ineligible_title: string;
    ineligible_body: string;
}

function draftFromScreener(screener: ScreenerT): ScreenerDraft {
    return {
        is_active: screener.is_active,
        label_id: screener.label_id,
        criteria: parseScreenerCriteria(screener.criteria),
        eligible_title: screener.eligible_title ?? "",
        eligible_body: screener.eligible_body ?? "",
        ineligible_title: screener.ineligible_title ?? "",
        ineligible_body: screener.ineligible_body ?? "",
    };
}

function ScreenerEditor({ form, formId, screener }: { form: FormBuilderData; formId: string; screener: ScreenerT }) {
    const copy = useScreenerCopy();
    const updateScreener = useUpdateScreener(formId);
    const deleteScreener = useDeleteScreener(formId);
    const [draft, setDraft] = useState(() => draftFromScreener(screener));
    const { data: diaryLabels = [] } = useSelectDiaryLabels();

    useEffect(() => {
        setDraft(draftFromScreener(screener));
        // Only reset when switching screener, not on every refetch of the one being edited.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screener.screener_id]);

    const allInputs = useMemo(() => form.pages.flatMap((page) => page.inputs as InputT[]), [form]);
    const inputsWithChoices = useInputsWithChoices(allInputs);
    const inputs = useMemo(() => {
        const questions = collectPriorInputs(
            form,
            undefined,
            new Map(inputsWithChoices.map((input) => [input.input_id, input]))
        );
        // Calculated scores can be checked only when the browser can work them out like the database does.
        const canCalculate = (BROWSER_SCORE_ENGINES as readonly string[]).includes(form.score_engine ?? "");
        const scores: PriorLogicInput[] = canCalculate
            ? (form.hiddenInputs ?? []).map((input) => ({
                  input_id: input.input_id,
                  label: input.input_label ?? "Score",
                  pageLabel: "Calculated",
                  input_key: "number",
                  options: [],
              }))
            : [];
        return [...questions, ...scores];
    }, [form, inputsWithChoices]);

    const autoSave = useBuilderAutoSave(draft, async (next) => {
        const inputIds = new Set(inputs.map((input) => input.input_id));
        const unknown = [...next.criteria.include, ...next.criteria.exclude].find(
            (condition) => !inputIds.has(condition.input_id)
        );
        if (unknown) throw new Error("A rule checks a question that is no longer on this form.");
        await updateScreener.mutateAsync({
            screenerId: screener.screener_id,
            update: {
                is_active: next.is_active,
                label_id: next.label_id,
                criteria: next.criteria,
                eligible_title: next.eligible_title.trim() || null,
                eligible_body: next.eligible_body.trim() || null,
                ineligible_title: next.ineligible_title.trim() || null,
                ineligible_body: next.ineligible_body.trim() || null,
            },
        });
    });

    const previewUrl = `${window.location.origin}/screen/${screener.screener_id}`;
    const setText =
        (key: "eligible_title" | "eligible_body" | "ineligible_title" | "ineligible_body") =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setDraft((prev) => ({ ...prev, [key]: event.target.value }));

    return (
        <div className="space-y-6 p-6">
            <div className="flex justify-between items-start gap-4">
                <div className="space-y-0.5 min-w-0">
                    <Label htmlFor="screener-active" className="text-sm">
                        Open to patients
                    </Label>
                    <p className="text-muted-foreground text-xs">
                        When off, links to this screener show a &quot;not working&quot; page.
                    </p>
                </div>
                <Switch
                    id="screener-active"
                    checked={draft.is_active}
                    onCheckedChange={(is_active) => setDraft((prev) => ({ ...prev, is_active }))}
                />
            </div>

            <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">Preview link</Label>
                <div className="flex gap-2">
                    <ui.Input readOnly value={previewUrl} className="font-mono text-xs" />
                    <ui.Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label="Copy link"
                        onClick={() => {
                            void navigator.clipboard.writeText(previewUrl);
                            toast.info("Link copied");
                        }}
                    >
                        <Copy size={16} />
                    </ui.Button>
                    <ui.Button type="button" variant="outline" size="icon" asChild>
                        <a href={previewUrl} target="_blank" rel="noreferrer" aria-label="Open screener">
                            <ExternalLink size={16} />
                        </a>
                    </ui.Button>
                </div>
                <p className="text-2xs text-muted-foreground text-pretty">
                    This link has no sign-up step. Patients get one from a template&apos;s invite link or QR code.
                </p>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="screener-label" className="text-sm">
                    Diary label
                </Label>
                <Select
                    value={draft.label_id ?? "none"}
                    onValueChange={(next) => setDraft((prev) => ({ ...prev, label_id: next === "none" ? null : next }))}
                >
                    <SelectTrigger id="screener-label" className="w-full sm:w-96" data-testid="screener-label-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">No label</SelectItem>
                        {diaryLabels.map((label) => (
                            <SelectItem key={label.label_id} value={label.label_id}>
                                {label.label_name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-2xs text-muted-foreground text-pretty">
                    Every response is saved under this label, eligible or not. Patients who sign up afterwards get it on
                    their diary, along with their screener answers.
                </p>
            </div>

            <ConditionList
                title="Eligible when all of these are true"
                description="A question the patient did not reach counts as not met."
                inputs={inputs}
                conditions={draft.criteria.include}
                onChange={(include) => setDraft((prev) => ({ ...prev, criteria: { ...prev.criteria, include } }))}
                testId="screener-include"
            />

            <ConditionList
                title="Not eligible if any of these are true"
                description="Only checked for questions the patient answered."
                inputs={inputs}
                conditions={draft.criteria.exclude}
                onChange={(exclude) => setDraft((prev) => ({ ...prev, criteria: { ...prev.criteria, exclude } }))}
                testId="screener-exclude"
            />

            <div className="space-y-3">
                <div>
                    <p className="font-medium text-sm">Eligible message</p>
                    <p className="text-muted-foreground text-xs">Shown above the button to sign up.</p>
                </div>
                <ui.Input
                    value={draft.eligible_title}
                    onChange={setText("eligible_title")}
                    placeholder={copy.eligibleTitle}
                    className="text-sm"
                    aria-label="Eligible title"
                />
                <ui.Textarea
                    value={draft.eligible_body}
                    onChange={setText("eligible_body")}
                    placeholder={copy.eligibleBody}
                    rows={3}
                    className="text-sm"
                    aria-label="Eligible message"
                />
            </div>

            <div className="space-y-3">
                <div>
                    <p className="font-medium text-sm">Not eligible message</p>
                    <p className="text-muted-foreground text-xs">Say who the patient can speak to.</p>
                </div>
                <ui.Input
                    value={draft.ineligible_title}
                    onChange={setText("ineligible_title")}
                    placeholder={copy.ineligibleTitle}
                    className="text-sm"
                    aria-label="Not eligible title"
                />
                <ui.Textarea
                    value={draft.ineligible_body}
                    onChange={setText("ineligible_body")}
                    placeholder={copy.ineligibleBody}
                    rows={3}
                    className="text-sm"
                    aria-label="Not eligible message"
                />
            </div>

            <div className="flex justify-between items-center gap-2 pt-2 border-t">
                <span className="text-2xs text-muted-foreground" aria-live="polite">
                    {autoSave.status === "saving"
                        ? "Saving screener…"
                        : autoSave.status === "unsaved"
                          ? "Screener not saved"
                          : autoSave.status === "error"
                            ? "Screener save failed"
                            : "Screener saved"}
                </span>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <ui.Button type="button" variant="ghost" size="sm" className="text-destructive">
                            Stop using as a screener
                        </ui.Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Stop using this form as a screener?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Its links stop working, templates that use it go straight to sign-up, and the answers
                                patients gave are deleted. The form itself stays.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteScreener.mutate(screener.screener_id)}>
                                Stop using
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
            {autoSave.status === "error" && autoSave.error instanceof Error ? (
                <p className="text-2xs text-destructive">{autoSave.error.message}</p>
            ) : null}
        </div>
    );
}

function ConditionList({
    title,
    description,
    inputs,
    conditions,
    onChange,
    testId,
}: {
    title: string;
    description: string;
    inputs: PriorLogicInput[];
    conditions: PageLogicCondition[];
    onChange: (next: PageLogicCondition[]) => void;
    testId: string;
}) {
    return (
        <div className="space-y-2" data-testid={testId}>
            <div>
                <p className="font-medium text-sm">{title}</p>
                <p className="text-muted-foreground text-xs">{description}</p>
            </div>
            {conditions.map((condition, index) => (
                <div
                    key={`${condition.input_id}-${index}`}
                    className="items-center gap-2 grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] bg-muted/40 p-2 rounded-md"
                >
                    <LogicConditionFields
                        inputs={inputs}
                        condition={condition}
                        onChange={(next) => onChange(conditions.map((item, i) => (i === index ? next : item)))}
                    />
                    <ui.Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6"
                        aria-label="Remove rule"
                        onClick={() => onChange(conditions.filter((_, i) => i !== index))}
                    >
                        <Trash2 size={14} />
                    </ui.Button>
                </div>
            ))}
            <button
                type="button"
                disabled={inputs.length === 0}
                className="flex items-center gap-1 hover:bg-muted disabled:opacity-40 px-2 py-1 rounded-md w-full min-w-0 text-muted-foreground hover:text-foreground text-xs text-left transition-colors disabled:pointer-events-none"
                onClick={() => {
                    const first = inputs[0];
                    if (!first) return;
                    onChange([
                        ...conditions,
                        {
                            input_id: first.input_id,
                            operator: defaultOperator(first.input_key),
                            value: defaultRuleValue(first.input_key),
                        },
                    ]);
                }}
            >
                <Plus size={14} className="shrink-0" /> Add rule
            </button>
        </div>
    );
}
